import React, {
  createContext,
  useMemo,
  useContext,
  useState,
  useCallback,
} from 'react';
import { useRouter } from 'next/router';
import {
  getCachedLayoutSettings,
  updateCachedLayoutSettings,
} from '../utils/LayoutSettingsStorage';
import { useEventsContext } from '../../../context/events/EventsContext';
import {
  EventName,
  PageType,
} from '../../../services/eventTrackingServiceTypes';
import { getBasePathFromRoute } from '../components/Navigation/NavigationTabs';

export enum LayoutElementState {
  OPEN = 'open',
  CLOSED = 'closed',
  HIDDEN = 'hidden',
}

export const BREAKPOINT_FOR_RIGHT_RAIL_COVER = 'sm';

/**
 * @description Attributes and functions used by the layout
 */
interface LayoutContextValue {
  /**
   * Given a boolean value (or the opposite of the current state), this function
   * expands or collapses the left rail and returns true/false if the left rail is open/closed.
   * Returns null if the left rail is hidden.
   */
  toggleLeftRail: (toggle?: boolean | any) => boolean | null;
  /**
   * Given a boolean value (or the opposite of the current state), this function
   * expands or collapses the right rail and returns true/false if the right rail is open/closed.
   * Returns null if the right rail is hidden.
   */
  toggleRightRail: (toggle?: boolean | any) => boolean | null;
  /**
   * Given a state value, this function updates the visibility of the left rail.
   * If the value is set to hidden/close, this updates the expand state to false.
   * If the value is set to open, this updates the expand state to true.
   */
  setLeftRailVisibility: (
    newState: LayoutElementState
  ) => LayoutElementState | null;
  /**
   * Given a state value, this function updates the visibility of the right rail.
   * If the value is set to hidden/close, this updates the expand state to false.
   * If the value is set to open, this updates the expand state to true.
   */
  setRightRailVisibility: (
    newState: LayoutElementState
  ) => LayoutElementState | null;
  /**
   * Given a boolean value, this function allows the right rail to be scrollable.
   * Returns null if the right rail is hidden.
   */
  toggleRightRailScrollable: (toggle?: boolean | any) => boolean | null;
  /**
   * The current state of the left rail
   */
  leftRailVisibility: LayoutElementState;
  /**
   * The current state of the right rail
   */
  rightRailVisibility: LayoutElementState;
  /**
   * Whether the right rail is hidden, i.e. not in the page
   */
  isRightRailHidden: boolean;
  /**
   * Whether the left rail is hidden, i.e. not in the page
   */
  isLeftRailHidden: boolean;
  /**
   * Whether the right rail is open
   */
  isRightRailOpen: boolean;
  /**
   * Whether the left rail is open
   */
  isLeftRailOpen: boolean;
  /**
   * Whether the right rail is scrollable
   */
  rightRailScrollable: boolean;
}

const LayoutContext = createContext<LayoutContextValue>({
  toggleLeftRail: () => false,
  toggleRightRail: () => false,
  setLeftRailVisibility: () => null,
  setRightRailVisibility: () => null,
  leftRailVisibility: LayoutElementState.CLOSED,
  rightRailVisibility: LayoutElementState.CLOSED,
  isLeftRailHidden: false,
  isRightRailHidden: false,
  isLeftRailOpen: false,
  isRightRailOpen: false,
  rightRailScrollable: false,
  toggleRightRailScrollable: () => null,
});

const getPageFromBasePath = (basePath: string): PageType | null => {
  const regex = /^\/dashboard\/(.+)$/;
  const match = basePath.match(regex);

  if (match && match[1]) {
    return match[1] as PageType;
  }

  return null; // Return null if there's no match
};

/**
 * @description a hook that provides the layout context
 */
export const useLayoutContext = () =>
  useContext<LayoutContextValue>(LayoutContext);

/**
 * @description Provides functions and state for the layout, currently only for the left and right rails.
 *            This also caches the previous visibility settings such that on reload it shows as expected.
 *
 * @todo -- we should scope the settings per page for the right rail.
 */
export default function LayoutContextProvider({
  children,
  defaultRightRailVisibility,
}: {
  children?: React.ReactNode;
  defaultRightRailVisibility?: LayoutElementState;
}) {
  const router = useRouter();
  const currentRoute = getBasePathFromRoute(router?.route || '');
  const page = getPageFromBasePath(currentRoute);
  const [eventTracking] = useEventsContext();
  const cachedSettingsMap = getCachedLayoutSettings();
  const [leftRailVisibility, setLeftRailVisibility] = useState(
    (cachedSettingsMap?.get('leftRailVisibility') as LayoutElementState) ||
      LayoutElementState.CLOSED
  );
  const [expandLeft, setExpandLeft] = useState(
    leftRailVisibility === LayoutElementState.OPEN
  );
  const handleToggleLeftRail = useCallback<
    LayoutContextValue['toggleLeftRail']
  >(
    toggle => {
      if (leftRailVisibility === LayoutElementState.HIDDEN) return null;

      const toggleResult = typeof toggle === 'boolean' ? toggle : !expandLeft;
      setExpandLeft(toggleResult);
      setLeftRailVisibility(
        toggleResult ? LayoutElementState.OPEN : LayoutElementState.CLOSED
      );

      if (page) {
        eventTracking.track({
          type: toggleResult ? EventName.LeftRailOpen : EventName.LeftRailClose,
          data: {
            page,
          },
        });
      }

      return toggleResult;
    },
    [leftRailVisibility, expandLeft, page, eventTracking]
  );
  const [rightRailScrollable, setRightRailScrollable] = useState(true);

  const handleSetLeftRailVisibility = useCallback<
    LayoutContextValue['setLeftRailVisibility']
  >(
    newState => {
      switch (newState) {
        case LayoutElementState.HIDDEN:
          setLeftRailVisibility(LayoutElementState.HIDDEN);
          setExpandLeft(false);
          break;
        case LayoutElementState.CLOSED:
          setLeftRailVisibility(LayoutElementState.CLOSED);
          setExpandLeft(false);
          break;
        case LayoutElementState.OPEN:
          setLeftRailVisibility(LayoutElementState.OPEN);
          setExpandLeft(true);
          break;
        default:
          return null;
      }
      return newState;
    },
    [setLeftRailVisibility, setExpandLeft]
  );

  const [rightRailVisibility, setRightRailVisibility] = useState(
    /**
     * @note Not caching the right rail as this needs to be scoped per page.
     * (cachedSettingsMap?.get('rightRailVisibility') as LayoutElementState) ||
     */
    defaultRightRailVisibility || LayoutElementState.CLOSED
  );
  const [expandRight, setExpandRight] = useState(
    rightRailVisibility === LayoutElementState.OPEN
  );
  const handleToggleRightRail = useCallback<
    LayoutContextValue['toggleRightRail']
  >(
    toggle => {
      if (rightRailVisibility === LayoutElementState.HIDDEN) return null;

      const toggleResult = typeof toggle === 'boolean' ? toggle : !expandRight;
      setExpandRight(toggleResult);
      setRightRailVisibility(
        toggleResult ? LayoutElementState.OPEN : LayoutElementState.CLOSED
      );

      if (page) {
        eventTracking.track({
          type: toggleResult
            ? EventName.RightRailOpen
            : EventName.RightRailClose,
          data: {
            page,
          },
        });
      }

      return toggleResult;
    },
    [eventTracking, expandRight, page, rightRailVisibility]
  );

  const handleSetRightRailVisibility = useCallback<
    LayoutContextValue['setRightRailVisibility']
  >(
    newState => {
      switch (newState) {
        case LayoutElementState.HIDDEN:
          setRightRailVisibility(LayoutElementState.HIDDEN);
          setExpandRight(false);
          break;
        case LayoutElementState.CLOSED:
          setRightRailVisibility(LayoutElementState.CLOSED);
          setExpandRight(false);
          break;
        case LayoutElementState.OPEN:
          setRightRailVisibility(LayoutElementState.OPEN);
          setExpandRight(true);
          break;
        default:
          return null;
      }
      return newState;
    },
    [setRightRailVisibility, setExpandRight]
  );

  const handleToggleRightRailScrollable = useCallback<
    LayoutContextValue['toggleRightRailScrollable']
  >(
    toggle => {
      if (rightRailVisibility === LayoutElementState.HIDDEN) return null;

      const toggleResult =
        typeof toggle === 'boolean' ? toggle : !rightRailScrollable;
      setRightRailScrollable(toggleResult);

      return toggleResult;
    },
    [rightRailScrollable, rightRailVisibility]
  );

  const value = useMemo<LayoutContextValue>(() => {
    updateCachedLayoutSettings({ leftRailVisibility, rightRailVisibility });

    return {
      toggleLeftRail: handleToggleLeftRail,
      toggleRightRail: handleToggleRightRail,
      setLeftRailVisibility: handleSetLeftRailVisibility,
      setRightRailVisibility: handleSetRightRailVisibility,
      leftRailVisibility,
      rightRailVisibility,
      isLeftRailHidden: leftRailVisibility === LayoutElementState.HIDDEN,
      isRightRailHidden: rightRailVisibility === LayoutElementState.HIDDEN,
      isLeftRailOpen: leftRailVisibility === LayoutElementState.OPEN,
      isRightRailOpen: rightRailVisibility === LayoutElementState.OPEN,
      rightRailScrollable,
      toggleRightRailScrollable: handleToggleRightRailScrollable,
    };
  }, [
    handleToggleLeftRail,
    handleToggleRightRail,
    handleSetLeftRailVisibility,
    handleSetRightRailVisibility,
    leftRailVisibility,
    rightRailVisibility,
    rightRailScrollable,
    handleToggleRightRailScrollable,
  ]);

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
}
